import { useTimeoutFn } from '@vueuse/core'

import { useIO } from '@/composables/io'
import { useDataStore } from '@/stores/data'
import { useStateStore } from '@/stores/state'
import { DataList, DataListEmbedded } from '@/components/types'

interface ResponseEntity {
  entity: string
  id: string
  rows?: ResponseRow[]
}

interface ResponseRow {
  entities: ResponseEntity[]
}

export function useActionLists() {

  const { performPostBucket } = useIO()

  const { get } = useDataStore()

  const { updateBlockGroupNameValue } = useStateStore()

  const findEntityIdInBucket = (bucket: DataList | {}, entity: string, id: string) => {
    if (!('rows' in bucket)) return undefined

    const matchRow = bucket.rows.find((r) => {
      const matchEntity = r.entities.find((e) => {
        if (e.entity === entity && parseInt(e.id, 10) === parseInt(id, 10)) return true
      })
      return matchEntity !== undefined
    })

    if (!matchRow) return undefined

    const matchEntity = matchRow.entities.find((e) => {
      if (e.entity === entity && parseInt(e.id, 10) === parseInt(id, 10)) return true
    })

    return matchEntity
  }

  const updateBucketActionListId = (bucket: DataList | {}, listMetaId: string, actionListId: string) => {
    if (!('rows' in bucket)) return

    for (const bucketRow of bucket.rows) {
      for (const bucketRowEntity of bucketRow.entities) {
        if (bucketRowEntity.type === 'list') {
          const entityBucketRW: DataListEmbedded | {} = get(listMetaId + '/' + bucketRowEntity.entity + '/' + bucketRowEntity.id)

          if ('rows' in entityBucketRW) {
            for (const bucketEntityRow of entityBucketRW.rows) {
              for (const bucketEntityRowEntity of bucketEntityRow.entities) {
                if (bucketEntityRowEntity.entry.state.actionlist) {
                  bucketEntityRowEntity.entry.state.actionlist.id = actionListId
                } else {
                  bucketEntityRowEntity.entry.state.actionlist = { id: actionListId }
                }
              }
            }

            entityBucketRW.selection.enabled = parseInt(actionListId, 10) > 0
            entityBucketRW.selection.header.show = parseInt(actionListId, 10) > 0
          }
        } else {
          if (bucketRowEntity.entry.state.actionlist) {
            bucketRowEntity.entry.state.actionlist.id = actionListId
          } else {
            bucketRowEntity.entry.state.actionlist = { id: actionListId }
          }
        }
      }
    }
  }

  interface States {
    companyState?: string | undefined
    projectState?: string | undefined
    contactState?: string | undefined
    applicantState?: string | undefined
    employeeState?: string | undefined
  }

  const updateBucketEntitySelection = (
    bucket: DataList | {},
    listMetaId: string,
    selectionColumName: string,
    { companyState = undefined, projectState = undefined, contactState = undefined, applicantState = undefined, employeeState = undefined }: States
  ) => {
    if (bucket && ('rows' in bucket) && bucket.rows.length > 0) {

      for (const bucketRow of bucket.rows) {

        for (const bucketRowEntity of bucketRow.entities) {

          const matchBucketRowEntity = findEntityIdInBucket(bucket, bucketRowEntity.entity, bucketRowEntity.id)

          if (matchBucketRowEntity) {

            if (matchBucketRowEntity.entity === 'company' && companyState !== undefined) {
              matchBucketRowEntity.entry.values[selectionColumName].value = companyState
            }

            if (matchBucketRowEntity.entity === 'project' && projectState !== undefined) {
              matchBucketRowEntity.entry.values[selectionColumName].value = projectState
            }

            if (matchBucketRowEntity.entity === 'applicant' && applicantState !== undefined) {
              matchBucketRowEntity.entry.values[selectionColumName].value = applicantState
            }

            if (matchBucketRowEntity.entity === 'employee' && employeeState !== undefined) {
              matchBucketRowEntity.entry.values[selectionColumName].value = employeeState
            }

            if (matchBucketRowEntity.entity === 'contact' && contactState !== undefined) {

              const entityBucketRW: DataListEmbedded | {} = get(listMetaId + '/' + matchBucketRowEntity.entity + '/' + matchBucketRowEntity.id)

              if (entityBucketRW && ('rows' in entityBucketRW) && entityBucketRW.rows.length > 0) {

                for (const bucketEntityRow of entityBucketRW.rows) {
                  for (const bucketEntityRowEntity of bucketEntityRow.entities) {

                    const matchBucketEntityRowEntity = findEntityIdInBucket(entityBucketRW, bucketEntityRowEntity.entity, bucketEntityRowEntity.id)

                    if (matchBucketEntityRowEntity) {
                      if (matchBucketEntityRowEntity.entity === 'contact') {
                        matchBucketEntityRowEntity.entry.values['checkbox_1'].value = contactState
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  const changeActionListCallback = (json: any, options: any) => {

    if (options.stopTimer) options.stopTimer()

    updateBlockGroupNameValue(
      options.blockMetaId,
      'global',
      'loading',
      false
    )

    if (!json.success) return
    if (options?.bucketList?.rows?.length === 0) return

    updateBucketActionListId(options.bucketList, options.listMetaId, options.actionListId)

    for (const responseRow of json.rows) {

      for (const responseRowEntity of responseRow.entities) {

        const matchRowEntity = findEntityIdInBucket(options.bucketList, responseRowEntity.entity, responseRowEntity.id)

        if (matchRowEntity) {

          if ('state' in responseRowEntity) {
            matchRowEntity.entry.values[options.selectionColumName].value = responseRowEntity.state
          }

          if ('rows' in responseRowEntity) {
            const entityBucketRW: DataListEmbedded | {} = get(options.listMetaId + '/' + matchRowEntity.entity + '/' + matchRowEntity.id)

            for (const responseRowEntityRow of responseRowEntity.rows) {
              const matchBucketRowEntity = findEntityIdInBucket(entityBucketRW, responseRowEntityRow.entity, responseRowEntityRow.id)

              if (matchBucketRowEntity) {
                matchBucketRowEntity.entry.values['checkbox_1'].value = responseRowEntityRow.state
              }
            }
          }
        }
      }
    }

    options.bucketList.selection.enabled = parseInt(json.actionListId, 10) > 0
    options.bucketList.selection.header.show = true
  }

  const changeActionList = (options: any) => {

    const { stop: stopTimer } = useTimeoutFn(() => {
      updateBlockGroupNameValue(
        options.blockMetaId,
        'global',
        'loading',
        true
      )
    }, 300)

    const listBucketRW: DataList | {} = get(options.listMetaId)

    const data = {
      actionListId: options.actionListId,
      rows: [] as ResponseRow[]
    }

    const responseRows: ResponseRow[] = []

    if ('rows' in listBucketRW && listBucketRW?.rows?.length > 0) {

      for (const resultsRow of listBucketRW.rows) {

        const responseEntities: ResponseEntity[] = []

        // first entity (with id 0) is always the entity with values for the primary row
        responseEntities.push({ entity: resultsRow.entities[0].entity, id: resultsRow.entities[0].id })

        for (const resultsRowEntities of resultsRow.entities) {
          if (resultsRowEntities.entry.state.collapsible.parent === 'open') {

            if (resultsRowEntities.type === 'list') {
              const entityRows: ResponseRow[] = []
              const entityBucketRW: DataListEmbedded | {} = get(options.listMetaId + '/' + resultsRowEntities.entity + '/' + resultsRowEntities.id)

              if ('rows' in entityBucketRW && entityBucketRW.rows.length > 0) {

                const entityRowEntities: ResponseEntity[] = []

                for (const bucketEntityRow of entityBucketRW.rows) {
                  for (const bucketEntityRowEntity of bucketEntityRow.entities) {
                    entityRowEntities.push({ entity: bucketEntityRowEntity.entity, id: bucketEntityRowEntity.id })
                  }
                }

                entityRows.push({ entities: entityRowEntities })
              }

              responseEntities.push({ entity: resultsRowEntities.entity, id: resultsRowEntities.id, rows: entityRows })

            } else {
              responseEntities.push({ entity: resultsRowEntities.entity, id: resultsRowEntities.id })
            }

          }
        }

        responseRows.push({
          entities: responseEntities
        })
      }

      data.rows = responseRows
    }

    performPostBucket(
      options.url_list_change,
      {},
      data,
      '',
      (data) => {
        changeActionListCallback(data, {
          actionListId: options.actionListId,
          listMetaId: options.listMetaId,
          selectionColumName: options.selectionColumName,
          blockMetaId: options.blockMetaId,
          bucketList: listBucketRW,
          stopTimer: stopTimer
        })
      }
    )
  }

  const addCompaniesToActionListCallback = (json: any, options: any) => {

    if (options.stopTimer) options.stopTimer()

    updateBlockGroupNameValue(
      options.blockMetaId,
      'global',
      'loading',
      false
    )

    if (!json.success) return

    const listBucketRW: DataList | {} = get(options.listMetaId)

    updateBucketEntitySelection(
      listBucketRW,
      options.listMetaId,
      options.selectionColumName,
      { companyState: 'on' }
    )

    if ('selection' in listBucketRW) listBucketRW.selection.header.selected = true
  }

  const addCompaniesToActionList = (options: any) => {

    const { stop: stopTimer } = useTimeoutFn(() => {
      updateBlockGroupNameValue(
        options.blockMetaId,
        'global',
        'loading',
        true
      )
    }, 300)

    performPostBucket(
      `${options.url_list_apply_all}/${options.actionListId}`,
      {},
      {},
      '',
      (data) => {
        addCompaniesToActionListCallback(data, {
          listMetaId: options.listMetaId,
          selectionColumName: options.selectionColumName,
          blockMetaId: options.blockMetaId,
          stopTimer: stopTimer
        })
      }
    )
  }

  const addApplicantsToActionListCallback = (json: any, options: any) => {

    if (options.stopTimer) options.stopTimer()

    updateBlockGroupNameValue(
      options.blockMetaId,
      'global',
      'loading',
      false
    )

    if (!json.success) return

    const listBucketRW: DataList | {} = get(options.listMetaId)

    updateBucketEntitySelection(
      listBucketRW,
      options.listMetaId,
      options.selectionColumName,
      { applicantState: 'on' }
    )

    if ('selection' in listBucketRW) listBucketRW.selection.header.selected = false
  }

  const addApplicantsToActionList = (options: any) => {

    const { stop: stopTimer } = useTimeoutFn(() => {
      updateBlockGroupNameValue(
        options.blockMetaId,
        'global',
        'loading',
        true
      )
    }, 300)

    performPostBucket(
      `${options.url_list_apply_all}/${options.actionListId}`,
      {},
      {},
      '',
      (data) => {
        addApplicantsToActionListCallback(data, {
          listMetaId: options.listMetaId,
          selectionColumName: options.selectionColumName,
          blockMetaId: options.blockMetaId,
          stopTimer: stopTimer
        })
      }
    )
  }

  const addEmployeesToActionListCallback = (json: any, options: any) => {

    if (options.stopTimer) options.stopTimer()

    updateBlockGroupNameValue(
      options.blockMetaId,
      'global',
      'loading',
      false
    )

    if (!json.success) return

    const listBucketRW: DataList | {} = get(options.listMetaId)

    updateBucketEntitySelection(
      listBucketRW,
      options.listMetaId,
      options.selectionColumName,
      { employeeState: 'on' }
    )

    if ('selection' in listBucketRW) listBucketRW.selection.header.selected = false
  }

  const addEmployeesToActionList = (options: any) => {

    const { stop: stopTimer } = useTimeoutFn(() => {
      updateBlockGroupNameValue(
        options.blockMetaId,
        'global',
        'loading',
        true
      )
    }, 300)

    performPostBucket(
      `${options.url_list_apply_all}/${options.actionListId}`,
      {},
      {},
      '',
      (data) => {
        addEmployeesToActionListCallback(data, {
          listMetaId: options.listMetaId,
          selectionColumName: options.selectionColumName,
          blockMetaId: options.blockMetaId,
          stopTimer: stopTimer
        })
      }
    )
  }

  const addContactsToActionListCallback = (json: any, options: any) => {

    if (options.stopTimer) options.stopTimer()

    updateBlockGroupNameValue(
      options.blockMetaId,
      'global',
      'loading',
      false
    )

    if (!json.success) return

    const listBucketRW: DataList | {} = get(options.listMetaId)

    updateBucketEntitySelection(
      listBucketRW,
      options.listMetaId,
      options.selectionColumName,
      { contactState: 'on' }
    )
  }

  const addContactsToActionList = (options: any) => {

    const { stop: stopTimer } = useTimeoutFn(() => {
      updateBlockGroupNameValue(
        options.blockMetaId,
        'global',
        'loading',
        true
      )
    }, 300)

    performPostBucket(
      `${options.url_list_apply_subentity_all}/${options.actionListId}`,
      {},
      {},
      '',
      (data) => {
        addContactsToActionListCallback(data, {
          listMetaId: options.listMetaId,
          selectionColumName: options.selectionColumName,
          blockMetaId: options.blockMetaId,
          stopTimer: stopTimer
        })
      }
    )
  }

  const clearActionListCallback = (json: any, options: any) => {

    if (options.stopTimer) options.stopTimer()

    updateBlockGroupNameValue(
      options.blockMetaId,
      'global',
      'loading',
      false
    )

    if (!json.success) return

    const listBucketRW: DataList | {} = get(options.listMetaId)

    updateBucketEntitySelection(
      listBucketRW,
      options.listMetaId,
      options.selectionColumName,
      { companyState: 'off', projectState: 'off', contactState: 'off', applicantState: 'off', employeeState: 'off' }
    )

    if ('selection' in listBucketRW) listBucketRW.selection.header.selected = false
  }

  const clearActionList = (options: any) => {

    const { stop: stopTimer } = useTimeoutFn(() => {
      updateBlockGroupNameValue(
        options.blockMetaId,
        'global',
        'loading',
        true
      )
    }, 300)

    performPostBucket(
      `${options.url_list_empty_all}/${options.actionListId}`,
      {},
      {},
      '',
      (data) => {
        clearActionListCallback(data, {
          listMetaId: options.listMetaId,
          selectionColumName: options.selectionColumName,
          blockMetaId: options.blockMetaId,
          stopTimer: stopTimer
        })
      }
    )
  }

  return {
    changeActionList,
    addCompaniesToActionList,
    addApplicantsToActionList,
    addEmployeesToActionList,
    addContactsToActionList,
    clearActionList,
    updateBucketEntitySelection
  }
}
